/**
 * The z-index of the highest modal mask currently on screen, or 0 for none.
 *
 * Extracted from `GlobalChatPanel` so it can be tested without mounting the
 * whole panel: this one number decides whether the chat is lifted over a
 * dialog's overlay and by how much, and it was wrong twice before it was right
 * (a hard-coded 1200 that lost on any page which had opened a few overlays
 * already, then a per-dialog claim that the fifteen untaught dialogs never
 * made — MYAC-1134).
 *
 * PrimeVue appends every mask as a direct child of `<body>`, so the caller
 * watches `document.body` for `childList` and calls this on each change.
 */
export const MASK_SELECTOR = '.p-dialog-mask'

/**
 * Marker for a mask belonging to a dialog the CHAT ITSELF opened (MYAC-1140).
 *
 * The lift above exists so a dialog opened on the PAGE cannot cover the chat.
 * A dialog opened FROM the chat is the opposite case: lifting over it clips the
 * dialog behind the panel and puts its footer buttons out of reach — observed
 * on a 13" screen, where the saved-prompt viewer lost its "Use" button. And it
 * cannot be fixed by giving that dialog a bigger z-index, because the panel
 * measures the top mask and adds one: the panel always wins by construction.
 *
 * So a chat-owned dialog suppresses the lift entirely rather than trying to
 * outrank it. Applied via a Dialog's `pt.mask.class`.
 */
export const CHAT_OWNED_MASK_CLASS = 'chat-owned-mask'

/**
 * Is one of the masks on screen owned by the chat itself?
 *
 * When it is, the panel must sit BELOW the masks rather than above them — the
 * user is working in a dialog the chat opened, and the chat being reachable
 * behind it is worth nothing next to the dialog being usable.
 */
export function hasChatOwnedMask(root: ParentNode = document): boolean {
  return root.querySelector(`${MASK_SELECTOR}.${CHAT_OWNED_MASK_CLASS}`) !== null
}

/** The HIGHEST mask on screen — several can coexist (a confirm over a dialog).
 * A mask whose z-index is absent or unparseable contributes nothing rather than
 * poisoning the maximum with `NaN`. */
export function topMaskZIndex(root: ParentNode = document): number {
  return [...root.querySelectorAll(MASK_SELECTOR)].reduce((top, el) => {
    const z = Number.parseInt(getComputedStyle(el).zIndex, 10)
    return Number.isFinite(z) ? Math.max(top, z) : top
  }, 0)
}
